import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from '../../features/ai-prompt-builder/redux/actions';
import * as collections from '../../features/collections/redux/actions';
import * as home from '../../features/home/redux/actions';
import CommonPageComponent from '../../common/ui/CommonPageComponent';
import LoadingCard from './LoadingCard';
import { toast } from 'react-toastify';
import { EXCLUDED_COLLECTIONS, validationSchema } from './utils';

export class PromptPagesList extends Component {
  static propTypes = {
    aiPromptBuilder: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    collections: PropTypes.object.isRequired,
  };

  state = {
    htmlGenerating: false,
    storedCollections: [],
    pagesList: [],
    autoSubmitted: false,
  };

  async componentDidMount() {
    try {
      const { actions, match, location, collections } = this.props;
      const { version, projectId } = match.params;
      const { state = {} } = location;
      const { userPrompt } = state;
      const { REACT_APP_AI_MODEL, REACT_APP_API_KEY } = process.env;

      // Step 1: Load collections
      const { data: storedCollections = [] } = await collections.loadProjectCollections({
        projectId,
        version,
      });
      this.setState({ storedCollections });

      // Step 2: Build data model names
      const dataModelName = storedCollections
        .filter(
          (collection) =>
            !EXCLUDED_COLLECTIONS.includes(collection.orgCollectionName.toLowerCase()),
        )
        .map((collection) => collection.orgCollectionName.toLowerCase());

      // Step 3: Fetch optimal pages
      const data = {
        userPrompt: userPrompt + ' having data models as ' + [...dataModelName],
        generatedCodeConfig: 'optimal_pages',
        codeGenerationModel: '',
        apiKey: REACT_APP_API_KEY,
        aiModel: REACT_APP_AI_MODEL,
        version,
      };

      await actions.fetchPages(data);

      // Step 4: Wait a moment for Redux to update, then auto-submit
      setTimeout(() => {
        const { aiPromptBuilder } = this.props;
        const { optimalPages } = aiPromptBuilder;
        if (optimalPages && optimalPages.data && optimalPages.data.pages &&optimalPages.data.pages.length && !this.state.autoSubmitted) {
          this.setState({ autoSubmitted: true });
          this.handleSubmit({ pages: optimalPages.data.pages });
        }
      }, 5000);
    } catch (error) {
      console.error('❌ Failed to fetch pages:', error);
    }
  }

  handleSubmit = async (values) => {
    const { storedCollections } = this.state;
    const { actions, match, history, location, home } = this.props;
    const { state = {} } = location;
    const { userPrompt, roles = [] } = state;
    const { version, projectId } = match.params;
    const { REACT_APP_AI_MODEL, REACT_APP_API_KEY } = process.env;

    try {
      roles.push('Super Admin');
      const aiPages = [];

      const { data: snippetData } = await home.loadSnippets({ projectId, version });
      const layouts = [];
      const snippetUUIDs = [];

      if (snippetData.length > 0) {
        snippetData.forEach((snippet) => {
          layouts.push({ value: snippet.uuid, label: snippet.name });
          snippetUUIDs.push({ layoutId: snippet.uuid, role: snippet.role });
        });
      }

      for (const value of values.pages) {
        this.setState({ htmlGenerating: true });

        const data = {
          userPrompt: `Create a ${value.name} having ${value.description} for ${userPrompt}`,
          generatedCodeConfig: 'generate_html_code',
          codeGenerationModel: '',
          apiKey: REACT_APP_API_KEY,
          aiModel: REACT_APP_AI_MODEL,
          version,
        };

        const htmlData = await actions.generateHtmlForPages(data);
        if (htmlData.data.success && htmlData.data.noCodeData) {
          const { noCodeHtml, noCodeCss } = htmlData.data.noCodeData;

          for (const role of roles) {
            const matchedSnippet = snippetUUIDs.find(
              (snippet) => snippet.role.toLowerCase() === role.toLowerCase(),
            );
            let pageName = `${role} ${value.name}`;
            if (role === 'Super Admin') {
              pageName = `Super Admin AI ${value.name}`;
            }
            if(this.state.pagesList.includes(pageName)){
              console.warn("This page name already exist",pageName)
              continue;
            }
            this.setState((prevState) => ({
              pagesList: [...prevState.pagesList, pageName]
            }));

            const pageData = {
              data: {
                name: pageName,
                slug: `${role.toLowerCase().replace(/\s+/g, '-')}-${value.name
                  .toLowerCase()
                  .replace(/\s+/g, '-')}-ai-page`,
                cloneFrom: '',
                restoreFrom: '',
                cloneSnippet: '',
                cloneSnippetSnapshot: '',
                collectionId: '',
                access: [role],
                titleTag: '',
                description: '',
                pageImage: '',
                isTitleFromCollection: false,
                isDescriptionFromCollection: false,
                isPageImageFromCollection: false,
                extraMetaTag: '',
                pageIndex: 9999,
                eventName: '',
                pageDirection: 'ltr',
                externalScriptURL: '',
                customScript: '',
                collectionFrom: 'COLLECTION',
                externalApiId: '',
                projectId,
                loadCustomScriptToBuilder: true,
                rlsFilter: null,
                pageLayoutId: matchedSnippet.layoutId || '',
                content: {
                  'nocode-html': noCodeHtml,
                  'nocode-components': '',
                  'nocode-css': noCodeCss,
                  'nocode-styles': '',
                },
                projectVersion: version,
              },
              version,
              projectId,
            };
            await actions.createPage(pageData);
            aiPages.push({
              name: pageName,
              slug: `${role.toLowerCase().replace(/\s+/g, '-')}-${value.name
                .toLowerCase()
                .replace(/\s+/g, '-')}-ai-page`,
            });
          }
        }
      }

      const collectionData = {};
      for (let i = 0; i < storedCollections.length; i++) {
        const collection = storedCollections[i];
        if (!EXCLUDED_COLLECTIONS.includes(collection.collectionName)) {
          collectionData[collection.uuid] = {
            selectedPage: { list: true, update: true, add: true },
            selectedLayout: layouts,
          };
        }
      }

      const createAppRequest = { collectionData, aiPages };
      await actions.createApp({ createAppRequest, projectId, version });
      await actions.updateLinkingToUuid({  projectId,version });
      
      toast.success('✅ AI Pages generated and app created successfully!');
      history.push(`/2.0/on-boarding/${version}/v/${projectId}/summary`);
    } catch (error) {
      console.error('Failed to generate html pages for the application', error);
      toast.error('❌ Failed to generate pages. Please try again.');
    } finally {
      this.setState({ htmlGenerating: false });
    }
  };

  render() {
    const { htmlGenerating } = this.state;
    const { aiPromptBuilder } = this.props;
    const { fetchPagesPending } = aiPromptBuilder;

    const loading = fetchPagesPending || htmlGenerating;

    return (
      <CommonPageComponent>
        {loading ? (
          <LoadingCard
            headerText={
              htmlGenerating
                ? 'Generating HTML Code for the Pages'
                : 'Identifying Pages for Your Application'
            }
            loadingText={
              htmlGenerating
                ? 'Please wait while we are generating pages for your application...'
                : 'Please wait while we are identifying optimal pages for your application...'
            }
            loadingFlag={loading}
            headerIcon="aiModelConfig"
          />
        ) : (
          <LoadingCard
            headerText="Finalizing..."
            loadingText="Preparing your AI application..."
            loadingFlag={true}
            headerIcon="aiModelConfig"
          />
        )}
      </CommonPageComponent>
    );
  }
}

function mapStateToProps(state) {
  return {
    aiPromptBuilder: state.aiPromptBuilder,
    collections: state.collections,
    home: state.home,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
    collections: bindActionCreators({ ...collections }, dispatch),
    home: bindActionCreators({ ...home }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PromptPagesList);
